import React, { ReactElement, useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import { Box, Button, Grid, Paper, Typography, useTheme } from '@material-ui/core';

import { currencyFormatter } from '../../../utils';
import { useStyles } from '../styles';
import { BillingData, Subscription } from '../../../types';
import { formatUtcToUkDate } from '../../../utils/dates';
import { AppLink, PageLoading } from '../../../components';
import config from '../../../api/config';

interface Props {
  companyName?: string;
  subscription?: Subscription;
  children?: ReactElement;
  loading?: boolean;
  billingData?: BillingData;
  openMessagingSubscriptionDialog?: () => void;
  openRemoveMessagingSubscriptionDialog?: () => void;
  openFormsSubscriptionDialog?: () => void;
  openRemoveFormsSubscriptionDialog?: () => void;
  messagingSubscription?: boolean;
  formsSubscription?: boolean;
  openActivateSubscriptionDialog?: () => void;
}

const UK_SHORT_DATE: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

const buttonStyle = { width: 160, minWidth: 160 };

const ModuleRow = ({
  moduleName,
  quantity,
  amount,
  buttonText,
  buttonVariant,
  onClick,
}: {
  moduleName: string;
  quantity: number | null;
  amount: string | number;
  buttonText?: string;
  buttonVariant?: 'outlined' | 'contained';
  onClick?: () => void;
}) => (
  <>
    <Grid item xs={3}>
      <Typography variant="body2">{moduleName}</Typography>
    </Grid>

    <Grid item xs={3}>
      <Typography variant="body2">{quantity === null ? '' : quantity}</Typography>
    </Grid>

    <Grid item xs={4}>
      <Typography variant="body2">{amount}</Typography>
    </Grid>

    {onClick && (
      <Grid item xs={2}>
        <Box display="flex" justifyContent="flex-end">
          <Button style={buttonStyle} variant={buttonVariant} size="small" onClick={onClick} color="primary">
            {buttonText}
          </Button>
        </Box>
      </Grid>
    )}
  </>
);

export const SubscriptionInfo: React.FC<Props> = ({
  companyName,
  subscription,
  children,
  loading,
  billingData,
  openMessagingSubscriptionDialog,
  openRemoveMessagingSubscriptionDialog,
  openFormsSubscriptionDialog,
  openRemoveFormsSubscriptionDialog,
  openActivateSubscriptionDialog,
  messagingSubscription,
  formsSubscription,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { active, ended, cancel_at_period_end, current_period_end } = subscription || {};
  const history = useHistory();

  const teamsBilling = useMemo(
    () => billingData?.items?.find(item => item.product_id === config.teamsStripeProductId),
    [billingData]
  );

  const payrollBilling = useMemo(
    () => billingData?.items?.find(item => item.product_id === config.payrollStripeProductId),
    [billingData]
  );

  const timeBilling = useMemo(
    () => billingData?.items?.find(item => item.product_id === config.timeStripeProductId),
    [billingData]
  );

  const messagesBilling = useMemo(
    () => billingData?.items?.find(item => item.product_id === config.messagesStripeProductId),
    [billingData]
  );

  const formsBilling = useMemo(
    () => billingData?.items?.find(item => item.product_id === config.formsStripeProductId),
    [billingData]
  );

  const payment: number = useMemo(() => (billingData?.total || 0) - (billingData?.tax || 0), [billingData]);

  const messagingModuleButtonText = useMemo(() => {
    return messagingSubscription ? 'Deactivate module' : 'Activate module';
  }, [messagingSubscription]);

  const formsModuleButtonText = useMemo(() => {
    return formsSubscription ? 'Deactivate module' : 'Activate module';
  }, [formsSubscription]);

  if (loading) {
    return <PageLoading />;
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between" flex={1}>
      <Paper className={classes.paper} style={{ minWidth: 640 }} variant="outlined">
        <Grid container className={classes.gridContainer}>
          <Grid item xs={6}>
            <Typography variant="h6">{companyName}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Box display="flex" alignItems="center" gridGap={4}>
              {active && cancel_at_period_end ? (
                <Typography className={classes.yellowText}>
                  Cancels on {formatUtcToUkDate(current_period_end)}
                </Typography>
              ) : ended ? (
                <Typography className={classes.yellowText}>
                  {active ? 'Expires' : 'Ended'} on {formatUtcToUkDate(ended)}
                </Typography>
              ) : (
                <>
                  <Typography
                    className={active ? classes.positiveText : classes.redText}
                    variant="subtitle2"
                  >
                    {active ? 'Subscribed' : 'Not subscribed'}
                  </Typography>
                  {active && <FiCheck color={theme.palette.success.main} size="20px" />}
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={3}>
            {children}
          </Grid>
        </Grid>

        <Grid container className={classes.card}>
          <Grid item xs={3}>
            <Typography variant="subtitle2">Module</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2">Max Number of users this month</Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography variant="subtitle2">Estimated cost</Typography>
          </Grid>

          <Grid item xs={2}>
            <Box />
          </Grid>

          <Box className={classes.gap} />

          <ModuleRow
            moduleName="Teams"
            quantity={teamsBilling?.quantity || 0}
            amount={currencyFormatter.format(teamsBilling?.amount || 0)}
            buttonText="Add/Remove Users"
            buttonVariant="outlined"
            onClick={() => {
              history.push('/user-management/teams');
            }}
          />
          <Box className={classes.gap} />

          <ModuleRow
            moduleName="Time & Attendance"
            quantity={timeBilling?.quantity || 0}
            amount={currencyFormatter.format(timeBilling?.amount || 0)}
            buttonText="Add/Remove Users"
            buttonVariant="outlined"
            onClick={() => {
              history.push('/time/users');
            }}
          />
          <Box className={classes.gap} />

          <Grid item xs={3}>
            <Box />
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2">Number of payments this month</Typography>
          </Grid>

          <Grid item xs={6}>
            <Box />
          </Grid>

          <Box className={classes.gap}></Box>

          <ModuleRow
            moduleName="Self Managed Payroll"
            quantity={payrollBilling?.quantity || 0}
            amount={currencyFormatter.format(payrollBilling?.amount || 0)}
            buttonText="Add/Remove Users"
            buttonVariant="outlined"
            onClick={() => {
              history.push('/user-management/payroll');
            }}
          />

          <Box className={classes.gap}></Box>

          <>
            <Grid item xs={3}>
              <Box />
            </Grid>

            <Grid item xs={3}>
              <Typography variant="subtitle2">Additional modules</Typography>
            </Grid>

            <Grid item xs={6}>
              <Box />
            </Grid>

            <ModuleRow
              moduleName="Messages"
              quantity={null}
              amount={currencyFormatter.format(messagesBilling?.amount || 0)}
              buttonText={messagingModuleButtonText}
              buttonVariant="contained"
              onClick={() => {
                if (!subscription?.active) {
                  openActivateSubscriptionDialog?.();
                } else if (messagingSubscription) {
                  openRemoveMessagingSubscriptionDialog?.();
                } else {
                  openMessagingSubscriptionDialog?.();
                }
              }}
            />

            <Box className={classes.gap}></Box>

            <ModuleRow
              moduleName="Forms"
              quantity={null}
              amount={currencyFormatter.format(formsBilling?.amount || 0)}
              buttonText={formsModuleButtonText}
              buttonVariant="contained"
              onClick={() => {
                if (!subscription?.active) {
                  openActivateSubscriptionDialog?.();
                } else if (formsSubscription) {
                  openRemoveFormsSubscriptionDialog?.();
                } else {
                  openFormsSubscriptionDialog?.();
                }
              }}
            />
          </>

          <Box className={classes.gap}></Box>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Estimated invoice for this month</Typography>
          </Grid>

          <Grid item xs={6}>
            {billingData?.period_start && billingData?.period_end && (
              <Box display="flex" flexDirection="column" gridGap={16}>
                <Typography className={classes.currentPeriod}>
                  Current payment period: {formatUtcToUkDate(billingData.period_start, UK_SHORT_DATE)} -{' '}
                  {formatUtcToUkDate(billingData.period_end, UK_SHORT_DATE)}
                </Typography>
                <Typography className={classes.currentPeriod}>VAT at 20%</Typography>
                <Typography className={classes.currentPeriod}>Amount due</Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={6}>
            <Box display="flex" flexDirection="column" gridGap={16}>
              <Typography variant="subtitle2">{currencyFormatter.format(payment)}</Typography>
              <Typography variant="subtitle2">{currencyFormatter.format(billingData?.tax || 0)}</Typography>
              <Typography variant="subtitle2">
                {currencyFormatter.format(billingData?.total || 0)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          Vyce subscriptions auto-renew monthly until the organisation is <AppLink to="#">cancelled</AppLink>
          . There is a 1 month notice period required for your cancellation. Contact support if you need
          help.
        </Typography>
      </Box>
    </Box>
  );
};
